import React, {useRef} from "react";
import useModal, {Modal} from "../../hook/useModal";
import {useIntl} from "react-intl";
import authRepository from "../../repository/AuthRepository";
import {API_HOST, CONSOLE_HOST, WEB_HOST} from "../../core/variables";
import closeIcon from "../../assets/svgs/icon_close_black_24.svg";
import {errorToast, successToast} from "../../toast";
import { AuthError } from "./index";

export const CHANGE_PASSWORD_MODAL_ID = "change-password-modal-id";

const ChangePasswordModal: React.FC = () => {
    const intl = useIntl();
    const modal = useModal();

    const oldPasswordRef = useRef<HTMLInputElement>(null);
    const newPasswordRef = useRef<HTMLInputElement>(null);
    const confirmNewPasswordRef = useRef<HTMLInputElement>(null);

    const onClickCancel = () => {
        modal.close(CHANGE_PASSWORD_MODAL_ID);
    }

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (oldPasswordRef.current && newPasswordRef.current && confirmNewPasswordRef.current) {
            const oldPassword = oldPasswordRef.current.value;
            const newPassword = newPasswordRef.current.value;
            const confirmNewPassword = confirmNewPasswordRef.current.value;

            if (!oldPassword || !newPassword || !confirmNewPassword) {
                errorToast(intl.formatMessage({id: "i0022"}));
                return;
            }

            if (newPassword !== confirmNewPassword) {
                errorToast(intl.formatMessage({id: "i0011"}));
                return;
            }

            if (oldPassword === newPassword) {
                errorToast(intl.formatMessage({id: "i0077"}));
                return;
            }

            try {
                const {data} = await authRepository.updatePassword(oldPassword, newPassword);
                const errorCode = data?.detail?.error_code;

                switch (errorCode) {
                    case AuthError.AUTH_003:
                        errorToast(intl.formatMessage({id: "i0076"}));
                        return;
                    case AuthError.AUTH_004:
                        errorToast(intl.formatMessage({id: "i0077"}));
                        return;
                }

                successToast(intl.formatMessage({id: "i0078"}));
                setTimeout(() => {
                    window.location.href = `${API_HOST}/auth/logout?redirect_uri=${CONSOLE_HOST}/login?redirectPath=${WEB_HOST}`
                }, 500);
            } catch (error) {
                errorToast(intl.formatMessage({id: "i0079"}));
            }
        }
    }

    return (
        <Modal id={CHANGE_PASSWORD_MODAL_ID}
               title={<p className="text-[20px] font-pretendard font-semibold px-[20px] pt-5 pb-[10px] text-console-black">{intl.formatMessage({id: "i0067"})}</p>}
               className="!p-0"
               size="sm"
               showCloseButton={true}
               customCloseButton={<img src={closeIcon} alt="close" className="absolute right-[10px] top-[10px] w-[24px] h-[24px] cursor-pointer"/>}
        >
            <form className="flex flex-col space-y-10 px-[24px] py-[16px] font-pretendard" onSubmit={onSubmit}>
                <div className="text-console-black whitespace-pre-line text-center">
                    <p>{intl.formatMessage({id: "i0080"})}</p>
                </div>

                <div className="space-y-4">
                    <label htmlFor="old-password"
                           className="font-semibold text-[15px] text-console-black">
                        {intl.formatMessage({id: "i0081"})}
                    </label>
                    <input type="password" name="old-password" id="old-password" placeholder="********"
                           ref={oldPasswordRef}
                           className="console-input"/>
                </div>

                <div className="space-y-2">
                    <label htmlFor="new-password"
                           className="font-semibold text-[15px] text-console-black">
                        {intl.formatMessage({id: "i0082"})}
                    </label>
                    <input type="password" name="new-password" id="new-password" placeholder="********"
                           pattern="^(?=.*[A-Za-z])(?=.*\d)(?=.*[$@$!%*#?&])[A-Za-z\d$@$!%*#?&]{8,15}$"
                           ref={newPasswordRef}
                           className="console-input peer invalid:is-invalid"/>
                    <p className="hidden peer-invalid:block console-input-invalid-text">
                        {intl.formatMessage({id: "i0084"})}
                    </p>
                </div>

                <div className="space-y-2">
                    <label htmlFor="confirm-new-password"
                           className="font-semibold text-[15px] text-console-black">
                        {intl.formatMessage({id: "i0083"})}
                    </label>
                    <input type="password" name="confirm-new-password" id="confirm-new-password" placeholder="********"
                           pattern="^(?=.*[A-Za-z])(?=.*\d)(?=.*[$@$!%*#?&])[A-Za-z\d$@$!%*#?&]{8,15}$"
                           ref={confirmNewPasswordRef}
                           className="console-input peer invalid:is-invalid"/>
                    <p className="hidden peer-invalid:block console-input-invalid-text">
                        {intl.formatMessage({id: "i0084"})}
                    </p>
                </div>

                <div className="flex space-x-[6px] justify-center">
                    <button type="button"
                            className="console-btn-lg-gray w-[118px]"
                            onClick={onClickCancel}>
                        {intl.formatMessage({id: "b0004"})}
                    </button>
                    <button type="submit" className="console-btn-lg-primary px-[18px] min-w-[118px]">
                        {intl.formatMessage({id: "i0075"})}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default ChangePasswordModal;
